import React from 'react';
import { FeedbackStatusProps } from '@/types/components';
import { StatusVariant } from '@/types/common';

// Predefined status mappings for common statuses
const defaultStatusMappings: Record<string, StatusVariant> = {
  // Success states
  complete: 'success',
  completed: 'success',
  active: 'success',
  approved: 'success',
  delivered: 'success',
  online: 'success',

  // Warning states
  'in delivery': 'warning',
  'in progress': 'warning',
  'pending pickup': 'warning',
  'picking up': 'warning',
  processing: 'warning',

  // Error states
  failed: 'error',
  error: 'error',
  rejected: 'error',
  offline: 'error',
  unavailable: 'error',

  // Info states
  allocating: 'info',
  queueing: 'info',
  scheduled: 'info',
  reviewing: 'info',

  // Neutral states
  canceled: 'neutral',
  cancelled: 'neutral',
  draft: 'neutral',
  idle: 'neutral',

  // Pending states
  pending: 'pending',
  waiting: 'pending',
  hold: 'pending',
};

export const FeedbackStatus: React.FC<FeedbackStatusProps> = ({
  status,
  variant,
  size = 'medium',
  customColors,
  className = '',
}) => {
  // Determine variant automatically if not provided
  const resolvedVariant =
    variant || defaultStatusMappings[status.toLowerCase()] || 'neutral';

  // Base styles
  const baseClasses = 'inline-flex items-center font-medium rounded-full';

  // Size variants
  const sizeClasses = {
    small: 'px-2 py-0.5 text-xs',
    medium: 'px-2.5 py-0.5 text-xs',
    large: 'px-3 py-1 text-sm',
  };

  // Color variants
  const variantClasses = {
    success: 'bg-green-100 text-green-800',
    warning: 'bg-yellow-100 text-yellow-800',
    error: 'bg-red-100 text-red-800',
    info: 'bg-blue-100 text-blue-800',
    neutral: 'bg-gray-100 text-gray-800',
    pending: 'bg-orange-100 text-orange-800',
  };

  // Build final classes
  let finalClasses = `${baseClasses} ${sizeClasses[size]}`;

  if (customColors) {
    finalClasses += ` ${className}`;
  } else {
    finalClasses += ` ${variantClasses[resolvedVariant]} ${className}`;
  }

  const style = customColors
    ? {
        backgroundColor: customColors.backgroundColor,
        color: customColors.textColor,
      }
    : undefined;

  return (
    <span className={finalClasses.trim()} style={style}>
      {status}
    </span>
  );
};

export default FeedbackStatus;
